import { Box, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import { Layout, useUserStore } from '@roc/feature-app-core';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../../stores/quoteTypeStore';
import { Button, StandardDialog } from '@roc/ui';
import {
  QuoteStep,
  PreflightStep as PrefligthStepEnum,
} from '../../utils/constants';
import { LoanDetailsCardProps } from '../summary/summaryStep';
import { useEffect, useState } from 'react';
import { EconomicsSummaryCard } from '../summary/components/economicsSummaryCard';
import {
  PropertyInformationFormProps,
} from '../propertyInformation/components/propertyInformationCard';
import { PrefligthPropertyCard } from './components/preflightPropertyCard';
import { PrefligthBorrowerCard } from './components/preflightBorrowerCard';
import { PrefligthClosingCard } from './components/preflightClosingCard';
import { PrefligthBrokerCard } from './components/preflightBrokerCard';
import { EarningsCards } from '../../components/earningsCards';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useEarningsCardSticky } from '../../hooks/useEarningsCardSticky';
import { useRoutes, useStore } from '@roc/client-portal-shared/hooks';
import { EntityInformationSummaryCard } from '../entityInformation/components/entityInformationSummaryCard';
import { DownloadQuoteAlert } from '../summary/components/downloadQuoteAlert';
import { LoanSubType, SeconAppraisalLoans, SECOND_APPRAISAL_FULL } from '@roc/feature-utils';
import { useHistory } from 'react-router';
import { PrefligthSecondAppraisalModal } from './components/preflightSecondAppraisalModal';
import { PrefligthValuationCard } from './components/preflightValuationCard';
import { LoanNotFullySubmittedCard } from '../common/loanNotFullySubmittedCard';

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: '34px',
  },
  paper: {},

  stepperWrapper: {
    flex: 1,
    padding: theme.spacing(3.5, 3.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1),
    },
  },
}));

export interface PreflightStepperProps {
  store: QuoteTypeStore;
  LoanDetailsCard: React.FC<LoanDetailsCardProps>;
  PropertyInformationForm: React.FC<PropertyInformationFormProps>;
}

export const PreflightStep = observer(
  ({
    store,
    LoanDetailsCard,
    PropertyInformationForm,
  }: PreflightStepperProps) => {
    const classes = useStyles();

    return (
      <Layout maxWidth={'lg'}>
        <Paper className={classes.paper} elevation={0}>
          <Box display="flex">
            <Box className={classes.stepperWrapper}>
              <Preflight
                store={store}
                LoanDetailsCard={LoanDetailsCard}
                PropertyInformationForm={PropertyInformationForm}
              />
            </Box>
          </Box>
        </Paper>
      </Layout>
    );
  }
);

export const ErrorsAlert = observer(({ errors }) => {
  return (
    <>
      {errors.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="error">
            <AlertTitle>ERRORS</AlertTitle>
            {errors.map(error => (
              <Grid>{error}</Grid>
            ))}
          </Alert>
        </Grid>
      )}
    </>
  );
});

const WarningsAlert = observer(({ warnings }) => {
  return (
    <>
      {warnings.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="warning">
            <AlertTitle>WARNINGS</AlertTitle>
            {warnings.map(warning => (
              <Grid>{warning}</Grid>
            ))}
          </Alert>
        </Grid>
      )}
    </>
  );
});

const Preflight = observer(
  ({
    store,
    LoanDetailsCard,
    PropertyInformationForm,
  }: PreflightStepperProps) => {
    const { earningsCardsStickyStyle } = useEarningsCardSticky();
    const { preflightStore, allErrors, allWarnings } = store;
    const { globalStore } = useStore();
    const { userStore } = useUserStore();
    const [secondAppraisalModalOpen, setSecondAppraisalModalOpen] = useState(false);
    const isTerm = store.loanSubtype === LoanSubType.SINGLE_PROPERTY || store.loanSubtype === LoanSubType.RENTAL_PORTFOLIO;
    const showValuationCard = store.showValuationCard;
    const enableSubmitLoan = !store.disableSubmitLoanButton;
    const hasBroker =
      store.brokerStore.brokerFormStore.form.fields.hasBroker.value;
    const { push } = useHistory();
    const { loanRoutesConfig } = useRoutes();

    useEffect(() => {
      if (preflightStore.nextStep === PrefligthStepEnum.SUBMISSION) {
        store.doSubmitLoanValidation();
      }
      if (preflightStore.nextStep === PrefligthStepEnum.BROKER && globalStore?.userFeatures?.isBroker) {
        preflightStore.goToNextStep()
      }
    }, [preflightStore.nextStep]);

    useEffect(() => {
      if (SeconAppraisalLoans.includes(store.loanSubtype)) {
        store.isSecondAppraisalNeeded()
      }
    }, []);

    const { propertyFormStores } = store.propertiesStore;
    const { borrowerFormStores } = store.borrowersStore;
    const { allowLoanPricing } = userStore;

    const validateOnSubmit = () => {
      if (preflightStore.validateAllSteps()) {
        submitLoan();
      }
    }

    const submitLoan = () => {
      if (store.isEditingLoan) {
        if (store.loanSubtype === LoanSubType.SINGLE_PROPERTY &&
          store.secondAppraisal?.secondAppraisalRequired &&
          store.secondAppraisal?.appraisalType !== SECOND_APPRAISAL_FULL &&
          (store.closingStore.closingFormStore.getValue('submitAppraisalWithTamarisk') || globalStore.userFeatures.tamariskAppraisal)
        ) {
          submitLoanAftherSecondAppraisalModal()
        } else {
          store.updateLoan(() => push(loanRoutesConfig.loans(`${store.loanId}`).children.dashboard.url));
        }
      } else {
        if (userStore.allowLoanPricing) {
          if (store.loanSubtype === LoanSubType.SINGLE_PROPERTY &&
            store.secondAppraisal?.secondAppraisalRequired &&
            store.secondAppraisal?.appraisalType !== SECOND_APPRAISAL_FULL &&
            (store.closingStore.closingFormStore.getValue('submitAppraisalWithTamarisk') || globalStore.userFeatures.tamariskAppraisal)
          ) {
            submitLoanAftherSecondAppraisalModal()
          } else {
            store.submitLoan();
          }
        } else {
          store.submitLoanApplication();
        }
      }
    };

    const submitLoanAftherSecondAppraisalModal = () => {
      if (store.isEditingLoan) {
        store.updateLoan(() => push(loanRoutesConfig.loans(`${store.loanId}`).children.dashboard.url));
      } else {
        if (userStore.allowLoanPricing) {
          store.submitLoan();
        } else {
          store.submitLoanApplication();
        }
      }
    };

    let submitText = 'SUBMIT';
    if (!store.isEditingLoan) {
      submitText = allowLoanPricing ? 'SAVE & SUBMIT AS LOAN' : 'SAVE & SUBMIT REQUEST';
    }

    return (
      <>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <LoanNotFullySubmittedCard />
          </Grid>
          <Grid item xs={12}>
            <EntityInformationSummaryCard />
          </Grid>
          {borrowerFormStores.map((borrowerFormStore, index) => (
            <Grid item xs={12}>
              <PrefligthBorrowerCard
                store={store}
                borrowerFormStore={borrowerFormStore}
                borrowerIndex={index}
              />
            </Grid>
          ))}
          {hasBroker && !globalStore?.userFeatures?.isBroker && (
            <Grid item xs={12}>
              <PrefligthBrokerCard store={store} />
            </Grid>
          )}
          {allowLoanPricing && (
            <Grid item xs={12} style={earningsCardsStickyStyle}>
              <EarningsCards store={store} />
            </Grid>
          )}
          {(!isTerm || (isTerm && allowLoanPricing)) && (
            <Grid item xs={12}>
              <LoanDetailsCard store={store} />
            </Grid>
          )}
          {allowLoanPricing && (
            <Grid item xs={12}>
              <EconomicsSummaryCard store={store} />
            </Grid>
          )}
          {propertyFormStores.map((propertyFormStore, index) => (
            <Grid item xs={12} key={propertyFormStore.form.meta.key}>
              <PrefligthPropertyCard
                store={store}
                propertyFormStore={propertyFormStore}
                propertyIndex={index}
              />
            </Grid>
          ))}
          {allowLoanPricing && (
            <Grid item xs={12}>
              <PrefligthClosingCard store={store} />
            </Grid>
          )}
          {showValuationCard && (
            <Grid item xs={12}>
              <PrefligthValuationCard store={store} />
            </Grid>
          )}
          {allowLoanPricing && (
            <Grid item xs={12}>
              <DownloadQuoteAlert />
            </Grid>
          )}
          <Grid item xs={12}>
            <ErrorsAlert errors={[...allErrors]} />
            <br />
            <WarningsAlert warnings={[...allWarnings]} />
          </Grid>
        </Grid>
        <Box pt={3}>
          <Grid container direction="row" justifyContent="center" spacing={2}>
            <Grid item xs={6} md={3}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => store.goToStep(QuoteStep.SUMMARY)}
                testId="previous"
              >
                RETURN TO QUOTE
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={validateOnSubmit}
                testId={'submit'}
                disabled={!enableSubmitLoan}
              >
                {submitText}
              </Button>
            </Grid>
          </Grid>
        </Box>
        <StandardDialog
          open={secondAppraisalModalOpen}
          handleClose={() => setSecondAppraisalModalOpen(false)}
          title={'How do you want to pay for this?'}
          dialogContent={<PrefligthSecondAppraisalModal store={store} />}
          removeContentBox={true}
          dialogActions={
            <Grid container justifyContent={'center'} style={{ gap: '16px' }}>
              <Button
                onClick={() => {
                  setSecondAppraisalModalOpen(false);
                }}
                variant="outlined"
                testId="cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const validation = store.preflightStore.secondAppraisalFormStore.runFormValidationWithMessage()
                  if (validation) {
                    setSecondAppraisalModalOpen(false);
                    submitLoanAftherSecondAppraisalModal()
                  }
                }}
                color="primary"
                variant="contained"
                testId="save"
              >
                Confirm
              </Button>
            </Grid>
          }
        />
      </>
    );
  }
);
